import { ArrowDown2 } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import { controlVariants } from '@/components/ui/control-variants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { richTextToPlainLabel } from '@/lib/rich-text';
import { cn } from '@/lib/utils';
import FieldLabel from './FieldLabel';
import TypePicker from './PresetPicker';
import YColumnList from './YColumnList';
import type { ChartTypeId } from '../chart-types/registry';
import type { ChartType, TableBlock } from '@/types/report-payload';

interface DataAndTypeStepProps {
    lockTableSelection: boolean;
    availableTables: TableBlock[];
    currentTableId: string;
    onTableChange: (id: string) => void;
    hasTable: boolean;
    columns: { id: string; label: string }[];
    defaultXAxisColumnId?: string | null;
    defaultYAxisColumnId?: string | null;
    xAxisColumnId: string;
    onXAxisChange: (id: string) => void;
    yAxisColumnIds: string[];
    multiSeries: boolean;
    onToggleYAxis: (id: string) => void;
    /** True when the active chart type supports a two-level grouped x-axis. */
    groupingSupported: boolean;
    groupColumnId: string;
    onGroupColumnChange: (id: string) => void;
    activeChartType: ChartType;
    onApplyType: (chartType: ChartTypeId) => void;
    onTypeHoverChange: (chartType: ChartTypeId | null) => void;
    /** All plottable x labels (unfiltered), for the hide-rows checklist. */
    xLabels: string[];
    /** Rows hidden from the chart, matched by trimmed x label. */
    excludedXLabels: string[];
    onToggleRow: (label: string) => void;
}

// Radix Select items cannot carry an empty value, so "no grouping" rides a sentinel.
const NO_GROUP_COLUMN = '__none__';

/** Step 1 of the wizard: the data (source table, X, Y) and the chart type. */
export default function DataAndTypeStep({
    lockTableSelection,
    availableTables,
    currentTableId,
    onTableChange,
    hasTable,
    columns,
    defaultXAxisColumnId,
    defaultYAxisColumnId,
    xAxisColumnId,
    onXAxisChange,
    yAxisColumnIds,
    multiSeries,
    onToggleYAxis,
    groupingSupported,
    groupColumnId,
    onGroupColumnChange,
    activeChartType,
    onApplyType,
    onTypeHoverChange,
    xLabels,
    excludedXLabels,
    onToggleRow,
}: DataAndTypeStepProps) {
    // The group column sits BESIDE the plotted data: x and y columns are excluded.
    const groupColumnOptions = columns.filter(
        (column) => column.id !== xAxisColumnId && !yAxisColumnIds.includes(column.id),
    );

    return (
        <div className="space-y-4">
            {!lockTableSelection && (
                <div>
                    <label className="mb-2 block text-sm font-medium text-foreground">
                        Tabela de origem
                    </label>
                    <Select value={currentTableId} onValueChange={onTableChange}>
                        <SelectTrigger>
                            <SelectValue placeholder="Selecione uma tabela" />
                        </SelectTrigger>
                        <SelectContent>
                            {availableTables.map((table) => (
                                <SelectItem key={table.id} value={table.id || ''}>
                                    {richTextToPlainLabel(table.description) || 'Tabela sem título'}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div>
                <label className="mb-2 block text-sm font-medium text-foreground">
                    Coluna do eixo X
                </label>
                <Select value={xAxisColumnId} onValueChange={onXAxisChange} disabled={!hasTable}>
                    <SelectTrigger>
                        <SelectValue placeholder="Selecione a coluna" />
                    </SelectTrigger>
                    <SelectContent>
                        {columns.map((column) => (
                            <SelectItem key={`x-${column.id}`} value={column.id}>
                                <div className="flex w-full items-center justify-between gap-2">
                                    <span>{column.label}</span>
                                    {column.id === defaultXAxisColumnId && (
                                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                            Sugerido
                                        </span>
                                    )}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <YColumnList
                columns={columns}
                selectedIds={yAxisColumnIds}
                defaultYAxisColumnId={defaultYAxisColumnId}
                multiSeries={multiSeries}
                disabled={!hasTable}
                onToggle={onToggleYAxis}
            />

            {xLabels.length > 0 && (
                <RowVisibilityField
                    xLabels={xLabels}
                    excludedXLabels={excludedXLabels}
                    disabled={!hasTable}
                    onToggleRow={onToggleRow}
                />
            )}

            {groupingSupported && (
                <div>
                    <FieldLabel
                        optional
                        hint="Agrupa as categorias por domínio — os valores iguais adjacentes formam um grupo."
                    >
                        Coluna de agrupamento
                    </FieldLabel>
                    <Select
                        value={groupColumnId || NO_GROUP_COLUMN}
                        onValueChange={(value: string) =>
                            onGroupColumnChange(value === NO_GROUP_COLUMN ? '' : value)
                        }
                        disabled={!hasTable}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Nenhuma" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={NO_GROUP_COLUMN}>Nenhuma</SelectItem>
                            {groupColumnOptions.map((column) => (
                                <SelectItem key={`group-${column.id}`} value={column.id}>
                                    {column.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <TypePicker
                activeChartType={activeChartType}
                disabled={!hasTable}
                onApply={onApplyType}
                onHoverChange={onTypeHoverChange}
            />
        </div>
    );
}

interface RowVisibilityFieldProps {
    xLabels: string[];
    excludedXLabels: string[];
    disabled: boolean;
    onToggleRow: (label: string) => void;
}

/**
 * Compact row-hide control: a Select-styled popover trigger summarizing how many
 * rows are hidden, expanding to the checklist on demand. Mirrors the multi-series
 * Y-column popover so the two dense selectors read the same.
 */
function RowVisibilityField({
    xLabels,
    excludedXLabels,
    disabled,
    onToggleRow,
}: RowVisibilityFieldProps) {
    const hiddenCount = xLabels.filter((label) => excludedXLabels.includes(label)).length;
    const summary =
        hiddenCount === 0
            ? 'Todas as linhas'
            : `${hiddenCount} oculta${hiddenCount > 1 ? 's' : ''}`;

    return (
        <div>
            <FieldLabel hint="Desmarque uma linha para escondê-la do gráfico (ex.: Total).">
                Linhas do gráfico
            </FieldLabel>
            <Popover modal>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled}
                        className={cn(
                            controlVariants(),
                            'items-center justify-between whitespace-nowrap focus:ring-2 focus:ring-ring/20',
                        )}
                    >
                        <span
                            className={cn(
                                'line-clamp-1 text-left',
                                hiddenCount === 0 && 'text-muted-foreground',
                            )}
                        >
                            {summary}
                        </span>
                        <ArrowDown2 className="ml-2 size-4 shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="max-h-64 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1"
                >
                    {xLabels.map((label) => {
                        const hidden = excludedXLabels.includes(label);
                        return (
                            <label
                                key={label}
                                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md p-2 text-left text-sm text-foreground transition-colors hover:bg-surface"
                            >
                                <Checkbox
                                    checked={!hidden}
                                    disabled={disabled}
                                    onCheckedChange={() => onToggleRow(label)}
                                />
                                <span className={hidden ? 'text-muted-foreground' : ''}>
                                    {label}
                                </span>
                            </label>
                        );
                    })}
                </PopoverContent>
            </Popover>
        </div>
    );
}
